import React, { FunctionComponent, MouseEventHandler } from 'react'
import { useTheme, Theme } from '@emotion/react'
import { CSSInterpolation } from '@emotion/serialize'
import facepaint from 'facepaint'

type Variant =
  | 'superTitle'
  | 'pageTitle'
  | 'headline'
  | 'subheading'
  | 'title'
  | 'subtitle'
  | 'lead'
  | 'body1'
  | 'body2'
  | 'secondary'
  | 'xxlarge'
  | 'xlarge'
  | 'large'
  | 'small'
  | 'xsmall'

type headlineMappingType = {
  // eslint-disable-next-line no-unused-vars
  [key in Variant]: string
}

/**
 * We are empirically mapping the variant property to a range of different DOM element types.
 * For instance, h1 to h6. You can use the `component` property to override.
 */
export const headlineMapping: headlineMappingType = {
  superTitle: 'h1',
  pageTitle: 'h1',
  headline: 'h2',
  subheading: 'h3',
  title: 'h4',
  subtitle: 'h5',
  lead: 'h6',
  body1: 'p',
  body2: 'p',
  secondary: 'p',
  xxlarge: 'p',
  xlarge: 'p',
  large: 'p',
  small: 'p',
  xsmall: 'p',
}

export interface Props {
  component?: React.ElementType
  className?: string
  variant?: Variant
  responsive?: boolean
  onClick?: MouseEventHandler
}

const Typography: FunctionComponent<Props> = ({
  component: componentProp,
  className: classNameProp,
  variant = 'body1',
  children,
  responsive,
  onClick,
}) => {
  const theme = useTheme()

  const styles = getVariantStyles(variant, theme)

  const Component: React.ElementType | string =
    componentProp || headlineMapping[variant] || 'span'

  return (
    <Component
      css={[styles, responsive && responsiveParentStyles]}
      className={classNameProp}
      onClick={onClick}
    >
      {responsive ? (
        <span
          css={facepaint([theme.selectors.col2, theme.selectors.col3])({
            fontSize: ['0.75em', '0.875em', '1em'],
            lineHeight: ['1.7em', '1.5em', '1.5em'],
          })}
          key="responsive"
        >
          {children}
        </span>
      ) : (
        children
      )}
    </Component>
  )
}
Typography.displayName = 'Typography'

export default Typography

/* eslint-disable @typescript-eslint/ban-types */
const baseStyles = (font: { family: { sans: string } }): object => ({
  fontFamily: font.family.sans,
  margin: 0,
  letterSpacing: '-.2px',
})

const headingStyles = (font: {
  family: { sans: string }
  weight: { medium: number }
}): object => ({
  ...baseStyles(font),
  fontWeight: font.weight.medium,
  lineHeight: '1.42857143em',
})
/* eslint-enable @typescript-eslint/ban-types */

const getVariantStyles = (variant: Variant, theme: Theme): CSSInterpolation => {
  const { font, selectors: mqs } = theme
  const mqSmall = facepaint([mqs.s])
  switch (variant) {
    case 'superTitle':
      return facepaint([mqs.xs, mqs.s, mqs.l, mqs.xl])({
        ...baseStyles(font),
        textTransform: 'uppercase',
        fontSize: [40, 50, 82, 96, 104],
        fontWeight: font.weight.heavy,
        lineHeight: '.884615385em',
        letterSpacing: '-.01em',
      })
    case 'pageTitle':
      return mqSmall({
        ...headingStyles(font),
        fontSize: [font.size.h3, font.size.h2],
        lineHeight: '1.225em',
      })
    case 'headline':
      return mqSmall({
        ...headingStyles(font),
        fontSize: [font.size.h3, font.size.h2],
        lineHeight: '1.225em',
      })
    case 'subheading':
      return mqSmall({
        ...headingStyles(font),
        fontSize: [font.size.h4, font.size.h3],
        lineHeight: '1.285em',
      })
    case 'title':
      return mqSmall({
        ...headingStyles(font),
        fontSize: [font.size.h5, font.size.h4],
        lineHeight: '1.222em',
      })
    case 'subtitle':
      return mqSmall({
        ...headingStyles(font),
        fontSize: [font.size.h6, font.size.h5],
        lineHeight: '1.222em',
      })
    case 'lead':
      return {
        ...headingStyles(font),
        fontSize: font.size.h6,
        lineHeight: '1.25em',
      }
    case 'body2':
      return {
        ...baseStyles(font),
        fontSize: 16,
        fontWeight: font.weight.medium,
      }
    case 'secondary':
      return {
        ...baseStyles(font),
        fontSize: 15,
      }
    case 'xxlarge':
      return {
        ...baseStyles(font),
        fontSize: 20,
      }
    case 'xlarge':
      return {
        ...baseStyles(font),
        fontSize: 18,
      }
    case 'large':
      return {
        ...baseStyles(font),
        fontSize: 16,
      }
    case 'small':
      return {
        ...baseStyles(font),
        fontSize: 14,
      }
    case 'xsmall':
      return {
        ...baseStyles(font),
        fontSize: 12,
      }
    default:
      // body1:
      return {
        ...baseStyles(font),
        fontSize: 16,
        fontWeight: font.weight.regular,
      }
  }
}

/**
 *  This is because the parent would still
 *  inherit the line height from the px value
 *  The class "responsive" is added to the children
 *  and will get new line height
 */
const responsiveParentStyles = {
  lineHeight: 0,
}
